import { InsuranceDetails, InsuranceOverview } from 'Api/insurance';
import { AlisProductName } from 'Components/InsuranceOverviewSection/components/InsuranceOverviewCard/insuranceOverviewCard.constants';
import { InsuranceStatus } from 'Constants/insuranceStatus.constants';
import { SystemName } from 'Constants/insuranceTypes.constants';
import {
  AkaProducts,
  BasicProducts,
  ChildProducts,
  DeregisterableVehicleProducts,
  ErgoProductName,
  ErgoProducts,
  NoVisibleDiscountProducts,
  PersonalAkaProducts,
  ProductName,
  ProductsWithBonus,
  ProductsWithFixedPrice,
  ProductsWithLimitedDetails,
  ProductsWithTFA,
  ReregisterableVehicleProducts,
  TravelProducts,
} from 'Constants/products.constants';
import { NewChildCoverageProduct } from 'Types/coverages/coverages.types';
import { getCoveragesFromCwp } from 'Utils/detailsCwp.utils';
import { isDefined } from 'Utils/type.utils';

/**
 * some insurances does not include discount
 */
export function noVisibleDiscountInsurance(insuranceType: string): boolean {
  return NoVisibleDiscountProducts.includes(insuranceType as ProductName);
}

export function isBasicProduct(insuranceType: string): boolean {
  return BasicProducts.includes(insuranceType as ProductName);
}

export const isAkaProduct = (insuranceProduct: string): boolean =>
  AkaProducts.includes(insuranceProduct as ProductName);

export const isAkaHealthProduct = (
  insuranceType: string | null | undefined,
): boolean =>
  isDefined(insuranceType) && insuranceType === ProductName.AKA_HEALTH;

export const isPersonalAkaInsurance = (insuranceProduct: string): boolean =>
  PersonalAkaProducts.includes(insuranceProduct as ProductName);

export function isTravelInsurance(insuranceType: string): boolean {
  return TravelProducts.includes(insuranceType as ProductName);
}

export function isCarInsurance(insuranceType: string): boolean {
  return insuranceType === ProductName.CAR;
}

export const isElectricScooterInsurance = (insuranceProduct: string): boolean =>
  insuranceProduct === ProductName.ELECTRIC_SCOOTER;

export const isHomeSellerInsuranceType = (
  insuranceType: string | undefined,
): boolean => isDefined(insuranceType) && insuranceType.includes('HOME_SELLER');

export const isErgoInsuranceProduct = (
  productName: string | undefined,
): boolean =>
  isDefined(productName) &&
  ErgoProducts.includes(productName as ErgoProductName);

export const isHomeBuyerInsuranceType = (
  insuranceType: string | undefined,
): boolean => isDefined(insuranceType) && insuranceType.includes('HOME_BUYER');

export const isHomeBuyerInterestInsuranceType = (
  insuranceType: string | undefined,
): boolean =>
  isDefined(insuranceType) && insuranceType.includes('HOME_BUYER_INTEREST');

export const isNordvikInsurance = (
  insuranceType: string | undefined,
): boolean =>
  isHomeBuyerInsuranceType(insuranceType) ||
  isHomeSellerInsuranceType(insuranceType);

export function isInsuranceWithTFA(insuranceType: string): boolean {
  return ProductsWithTFA.includes(insuranceType as ProductName);
}

export function isDeregisterableVehicleInsurance(
  insuranceType: string,
): boolean {
  return DeregisterableVehicleProducts.includes(insuranceType as ProductName);
}

export function isReregisterableVehicleInsurance(
  insuranceType: string,
): boolean {
  return ReregisterableVehicleProducts.includes(insuranceType as ProductName);
}

export function isAnyChildInsurance(insuranceType: string): boolean {
  return ChildProducts.includes(insuranceType as ProductName);
}

export function isInsuranceWithBonus(
  insuranceType: string | undefined,
): boolean {
  return (
    isDefined(insuranceType) &&
    ProductsWithBonus.includes(insuranceType as ProductName)
  );
}

export function isInsuranceWithLimitedDetails(insuranceType: string): boolean {
  return ProductsWithLimitedDetails.includes(insuranceType as ProductName);
}

export function shouldFetchDetails(insurance: InsuranceOverview): boolean {
  const isInsuranceStatusesWeShouldFetchDetailsFor =
    insurance.insuranceStatus === InsuranceStatus.ACTIVE ||
    insurance.insuranceStatus === InsuranceStatus.UNDER_CANCELLATION ||
    insurance.insuranceStatus === InsuranceStatus.UNDER_CHANGE;

  const isPolicyInF2100 = insurance.systemName === SystemName.SKD;

  return isInsuranceStatusesWeShouldFetchDetailsFor && isPolicyInF2100;
}

export function isInsuranceWithFixedPrice(insuranceType: string): boolean {
  return ProductsWithFixedPrice.includes(insuranceType as ProductName);
}

export const isAutoProduct = (productName: ProductName): boolean =>
  isMotorcycleProduct(productName) || isCarProduct(productName);

export const isCarProduct = (product: ProductName): boolean =>
  product === ProductName.CAR;

export const isMotorcycleProduct = (product: ProductName): boolean =>
  product === ProductName.MOTORCYCLE;

export const isHouseProduct = (product: ProductName): boolean =>
  product === ProductName.HOUSE;

export const isCabinProduct = (product: ProductName): boolean =>
  product === ProductName.CABIN;

export const isHouseContentsProduct = (product: ProductName): boolean =>
  product === ProductName.HOUSE_CONTENTS;

export const isNewChildProduct = (product: ProductName): boolean =>
  product === ProductName.NEW_CHILD;

export const isChildProduct = (product: ProductName): boolean =>
  product === ProductName.CHILD;

export const isAuroraChildCompleteProduct = (product: ProductName): boolean =>
  product === ProductName.AURORA_CHILD_COMPLETE;

export const isAnyHouseContentsProduct = (product: ProductName): boolean =>
  product === ProductName.AKA_HOUSE_CONTENTS ||
  product === ProductName.HUSEIERNE_HOUSE_CONTENTS ||
  product === ProductName.HOUSE_CONTENTS ||
  product === ProductName.HOUSE_CONTENTS_AGREEMENT ||
  product === ProductName.HOUSE_CONTENTS_STB_AGREEMENT;

export const isHomeSellerProduct = (
  product: ProductName | undefined,
): boolean => isDefined(product) && product === ProductName.HOME_SELLER;

export const isHomeBuyerInterestProduct = (
  product: ProductName | undefined,
): boolean => isDefined(product) && product === ProductName.HOME_BUYER_INTEREST;

export const isHomeBuyerMoveProduct = (
  product: ProductName | undefined,
): boolean => isDefined(product) && product === ProductName.HOME_BUYER_MOVE;

export const isHomeBuyerHouseProduct = (
  product: ProductName | undefined,
): boolean => isDefined(product) && product === ProductName.HOME_BUYER_HOUSE;

export const isHomeBuyerHouseContentsProduct = (
  product: ProductName | undefined,
): boolean =>
  isDefined(product) && product === ProductName.HOME_BUYER_HOUSE_CONTENTS;

export const isHomeBuyerHouseOrCabinOrContents = (
  product?: ProductName,
): boolean =>
  isDefined(product) &&
  (isHomeBuyerHouseContentsProduct(product) ||
    isHomeBuyerHouseProduct(product));

export const isHomeBuyerProduct = (product: ProductName | undefined): boolean =>
  isDefined(product) &&
  (isHomeBuyerInterestProduct(product) ||
    isHomeBuyerMoveProduct(product) ||
    isHomeBuyerHouseProduct(product) ||
    isHomeBuyerHouseContentsProduct(product));

export const isNewChildWithHalfG = (insurance: InsuranceDetails): boolean => {
  const coverages = getCoveragesFromCwp(insurance);
  return (
    coverages.filter(
      (coverage) =>
        coverage.coverageProduct ===
        NewChildCoverageProduct.NEWCHILD_DISABILITY_PENSION_HALF_G,
    ).length > 0
  );
};

export const isAlisChildSuperProduct = (productName: string): boolean =>
  productName === AlisProductName.CHILD_SUPER;
